import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Eye, EyeOff, Brain } from 'lucide-react';

import { Link, useNavigate } from 'react-router-dom';
import { authAPI, handleAuthSuccess } from '../services/api';
import GoogleLoginButton from '../components/GoogleLoginButton';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Color constants matching Header component
  const marineBlue = '#1e5f74';
  const lightBlue = '#5fb3d4';

  const handleGoogleSuccess = (credentialResponse) => {
    console.log('Google login success:', credentialResponse);
    // You can call your backend here with the token
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
  };

  const handleAuthSuccessWithRedirect = (response, navigate) => {
    const redirect = localStorage.getItem('postLoginRedirect');
    if (redirect) {
      localStorage.removeItem('postLoginRedirect');
      handleAuthSuccess(response, (path) => navigate(redirect));
      navigate(redirect);
    } else {
      handleAuthSuccess(response, navigate);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
      if (!email || !password) {
        setError('Please fill in all fields');
        return;
    }

    setLoading(true);
    try {
        const response = await authAPI.login(email, password);
        handleAuthSuccessWithRedirect(response, navigate);
    } catch (error) {
      console.error('Login error:', error);
      if (error.response && error.response.status === 400) {
        setError('Invalid email or password');
      } else {
        setError(error.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex flex-col lg:flex-row overflow-x-hidden">
      {/* Left Side: Blue BG with White Login Card */}
      <div className="w-full lg:w-1/2 bg-blue-600 flex justify-center lg:justify-end py-8 ">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-xl bg-white shadow-xl p-8 flex flex-col justify-center min-h-[400px] sm:min-h-[500px] lg:min-h-[600px]"
        >
          <div>
            {/* Company Logo */}
          <div className="flex items-center justify-center space-x-2 mb-14">
            <Brain className="h-8 w-8" style={{color: lightBlue}} />
            <span className="text-2xl font-bold">
              <span style={{color: lightBlue}}>ADHD</span>
              <span style={{color: marineBlue}}>RapidCare</span>
            </span>
          </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2 text-center">Login</h2>
            <p className="text-gray-600 text-sm mb-6 text-center">Start your ADHD assessment journey online</p>

            {/* Error Display */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Email Input */}
            <div className="mt-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
             <input
  type="email"
  name="email"
  id="email"
  placeholder="john@email.com"
  value={email}
  onChange={(e) => {
    setEmail(e.target.value);
    if (error) setError('');
  }}
  required
  className={`w-full px-3 py-2.5 border rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 sm:text-sm 
    ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
/>

              
            </div>

            {/* Password Input */}
        <div className="mt-4 relative">
  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>

 <input
  type={showPassword ? "text" : "password"}
  name="password"
  
  id="password"
  placeholder="Your Password"
  value={password}
  onChange={(e) => {
    setPassword(e.target.value);
    if (error) setError('');
  }}
  required
  className={`w-full px-3 py-2.5 border rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 sm:text-sm pr-10 
    ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
/>

  {/* Eye toggle button */}
  <button
    type="button"
    onClick={() => setShowPassword((prev) => !prev)}
    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 mt-3 hover:text-gray-700 focus:outline-none"
  >
    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
  </button>
</div>


            {/* Forgot Password Link */}
              <div className="mt-3 text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                >
                  Forgot Password?
                </Link>
              </div>

            <div className="w-full pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2.5 px-4 mt-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </div>

            {/* Join/Sign Up Section */}
            <div className="mt-4 text-center hidden">
              <p className="text-sm text-gray-600">
                Don't have an account? <Link to="/register?redirect=/care-plan#pricing" className="font-medium text-blue-600 hover:text-blue-500">Join now</Link>
              </p>
            </div>

            {/* Google Sign In */}
            {/* <div className="mt-4">
              <GoogleLoginButton
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                disabled={loading}
                text="Sign in with Google"
                theme="outline"
                size="large"
                className="w-full"
              />
            </div> */}

            {/* <p className="text-center text-sm mt-4 text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-600 hover:underline font-medium">Sign up</Link>
            </p> */}
          </div>
        </form>
      </div>

      {/* Right Side: Info Card */}
      <div className="w-full lg:w-1/2 bg-gray-200 flex justify-center lg:justify-start py-8 ">
        <div className="w-full max-w-xl bg-blue-600 text-white shadow-xl p-8 min-h-[400px] sm:min-h-[500px] lg:min-h-[600px]">
          <div className="flex flex-col justify-center h-full">
            {/* Profile Image */}
            <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white/30 shadow-lg mb-6 mx-auto">
              <img src="/images/signup2.jfif" alt="Doctor" className="w-full h-full object-cover" />
            </div>

            {/* Heading */}
            <h2 className="text-2xl font-bold text-center mb-3">Welcome to ADHDRapidCare</h2>
            <p className="text-base text-white/90 text-center mb-8">Your journey to better mental health starts here</p>

            {/* Feature List */}
            <div className="space-y-6 text-left w-full max-w-sm mx-auto">
              {/* Feature 1 */}
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                  <img src="/images/SVG1.png" alt="Brain icon" className="w-5 h-5 object-contain" />
                </div>
                <div>
                  <p className="text-base font-semibold">AI-Powered Diagnosis</p>
                  <p className="text-sm text-white/80">Advanced technology meets clinical expertise</p>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                  <img src="/images/SVG 2.png" alt="Brain icon" className="w-5 h-5 object-contain" />
                </div>
                <div>
                  <p className="text-base font-semibold">Licensed Medical Professionals</p>
                  <p className="text-sm text-white/80">Care from certified healthcare providers</p>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                  <img src="/images/SVG 3.png" alt="Brain icon" className="w-5 h-5 object-contain" />
                </div>
                <div>
                  <p className="text-base font-semibold">Personalised Care Plans  </p>
                  <p className="text-sm text-white/80">Tailored treatment for your unique needs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
